/**
 * Pure helpers and shared constants for the itinerary list panel.
 *
 * @remarks Colocated with `itineraryList/` components; not part of global `src/tools/`.
 */
import axios from "axios";
import { TransportMode } from "../../../services/ItineraryService";
import { typeToOrder } from "../../../tools/Itinerary";
import { type SavedListFilterTabId } from "../../../tools/savedItineraryDisplay.tools";
import { ItineraryType, type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";

/** Lifecycle status for a per-itinerary offer fetch. */
export type OfferFetchStatus = "idle" | "loading" | "success" | "error";

/** One filter-tab definition for the saved itinerary list. */
export interface SavedListFilterTabDefinition {
    "id":    SavedListFilterTabId;
    "label": string;
}

/** Filter tabs shown above the saved-itinerary list. */
export const SAVED_LIST_FILTER_TABS: readonly SavedListFilterTabDefinition[] = [
    {
        "id":    "all",
        "label": "Tous",
    },
    {
        "id":    "itineraries",
        "label": "Itinéraires",
    },
];

/**
 * Whether an unknown error is an aborted / cancelled HTTP request.
 *
 * @param error Unknown catch value.
 * @returns `true` for DOM AbortError or Axios cancel errors.
 */
export const isAbortError = ( error: unknown ): boolean => {
    if ( error instanceof DOMException && error.name === "AbortError" ) {
        return true;
    }

    return axios.isCancel( error );
};

/**
 * Whether a carpool itinerary is still incomplete and must not be selectable.
 *
 * @param itinerary Itinerary candidate from search/completion.
 * @returns `true` when `finalized` is explicitly `false`.
 */
export const isCarpoolIncomplete = ( itinerary: ItineraryDTO ): boolean => {
    return itinerary.finalized === false;
};

/**
 * Sorts itineraries by stable type order (mutates the input array).
 *
 * @param itineraries Itineraries to sort in place.
 * @returns The same array reference after sorting.
 */
export const sortItineraries = ( itineraries: ItineraryDTO[] ): ItineraryDTO[] => {
    return itineraries.sort( ( a, b ) => typeToOrder( a.type ?? ItineraryType.MOTIS ) - typeToOrder( b.type ?? ItineraryType.MOTIS ) );
};

/**
 * Converts an id-keyed itinerary map into a type-sorted array.
 *
 * @param itinerariesById Map of itineraries keyed by id, or null/undefined.
 * @returns Sorted itineraries, or an empty array when the map is missing.
 */
export const toSortedArray = ( itinerariesById: Record<string, ItineraryDTO> | null | undefined ): ItineraryDTO[] => {
    if ( itinerariesById === null || itinerariesById === undefined ) {
        return [];
    }

    return sortItineraries( Object.values( itinerariesById ) );
};

/**
 * Merges a patch map into the current itinerary list, then re-sorts by type.
 *
 * @param currentItineraries Current displayed/search itineraries.
 * @param patchById Partial itinerary replacements keyed by id.
 * @returns Updated list (or the original reference when the patch is empty/missing).
 */
export const mergeItineraryPatchById = (
    currentItineraries: ItineraryDTO[],
    patchById: Record<string, ItineraryDTO> | null | undefined,
): ItineraryDTO[] => {
    if ( patchById === null || patchById === undefined ) {
        return currentItineraries;
    }

    const patchEntries = Object.entries( patchById );
    if ( patchEntries.length === 0 ) {
        return currentItineraries;
    }

    const nextItineraries = currentItineraries.map( ( itinerary ) => {
        const patchedItinerary = patchById[itinerary.id];

        return patchedItinerary ?? itinerary;
    } );

    return sortItineraries( nextItineraries );
};

/**
 * Removes pricing segments, evaluation, offer, and leg-level pricing fields from an itinerary.
 *
 * @param itinerary Source itinerary snapshot.
 * @returns A new itinerary without pricing/offer enrichment.
 */
export const stripItineraryPricingAndOfferData = ( itinerary: ItineraryDTO ): ItineraryDTO => {
    const strippedItinerary: ItineraryDTO = {
        ...itinerary,
        "legs": itinerary.legs.map( ( leg ) => {
            const nextLeg = { ...leg };
            delete nextLeg.price;
            delete nextLeg.priceEvaluationSegmentId;

            return nextLeg;
        } ),
    };

    delete strippedItinerary.price;
    delete strippedItinerary.pricingSegments;
    delete strippedItinerary.pricingEvaluation;
    delete strippedItinerary.offer;

    return strippedItinerary;
};

/**
 * Removes only the sales offer projection from an itinerary.
 *
 * @param itinerary Source itinerary snapshot.
 * @returns A new itinerary without `offer`.
 */
export const stripItineraryOfferData = ( itinerary: ItineraryDTO ): ItineraryDTO => {
    const strippedItinerary: ItineraryDTO = { ...itinerary };
    delete strippedItinerary.offer;

    return strippedItinerary;
};

/**
 * Replaces one itinerary inside a list by id.
 *
 * @param currentItineraries Current itinerary list.
 * @param nextItinerary Replacement itinerary.
 * @returns Updated list when a matching id exists; otherwise a shallow copy of the original list.
 */
export const replaceItineraryInList = (
    currentItineraries: readonly ItineraryDTO[],
    nextItinerary: ItineraryDTO,
): ItineraryDTO[] => {
    const hasMatch = currentItineraries.some( ( itinerary ) => itinerary.id === nextItinerary.id );
    if ( !hasMatch ) {
        return [ ...currentItineraries ];
    }

    return currentItineraries.map( ( itinerary ) => {
        return itinerary.id === nextItinerary.id ? nextItinerary : itinerary;
    } );
};

/**
 * Builds transport modes for itinerary search from user mobility preferences.
 *
 * @param hasPersonalBike Whether personal bike routing is enabled.
 * @param hasRentalTypes Whether at least one rental vehicle type is enabled.
 * @param isCarpoolingEnabled Whether carpool partner search is enabled.
 * @returns Transport modes sent in itinerary search requests.
 */
export const buildSearchModes = (
    hasPersonalBike: boolean,
    hasRentalTypes: boolean,
    isCarpoolingEnabled: boolean,
): TransportMode[] => {
    const baseModes: TransportMode[] = [
        TransportMode.FOOT,
        TransportMode.BIKE,
        ...isCarpoolingEnabled ? [ TransportMode.CARPOOL ] : [],
        TransportMode.TRANSIT,
    ];
    const filteredBaseModes = hasPersonalBike
        ? baseModes
        : baseModes.filter( ( mode ) => mode !== TransportMode.BIKE );

    if ( hasRentalTypes !== true ) {
        return filteredBaseModes;
    }

    return filteredBaseModes.includes( TransportMode.RENTAL )
        ? filteredBaseModes
        : [ ...filteredBaseModes, TransportMode.RENTAL ] as TransportMode[];
};

/**
 * Whether a MYCAR itinerary has the fields required to show the car proposition.
 *
 * @param itinerary Candidate MYCAR itinerary from search.
 * @returns `true` when duration and start/end times are defined.
 */
export const isCompleteMyCarItinerary = ( itinerary: ItineraryDTO ): boolean => {
    return itinerary.duration !== undefined && itinerary.startTime !== undefined && itinerary.endTime !== undefined;
};

/**
 * Narrows a bike speed preference to the backend-supported discrete values.
 *
 * @param speed Raw speed from bike prefs.
 * @returns `true` when speed is 15, 25, or 45 km/h.
 */
export const isValidBikeSpeed = ( speed: number ): speed is 15 | 25 | 45 => {
    return speed === 15 || speed === 25 || speed === 45;
};
